import { Meal } from "@/clients/models/Meal";
import { MealTime } from "@/clients/models/MealTime";
import { ChevronDownIcon } from "@/components/icons/chevron-down-icon";
import { BaseModal } from "@/components/modal/base-modal";
import { SegmentedControl } from "@/components/toggle/segmented-control";
import { TextButton } from "@/components/ui/buttons/TextButton";
import {
  SAVED_MEALS_TAB_OPTIONS,
  SavedMealsTabValue,
} from "@/features/meal-plan/constants/saved-meals-tabs";
import { useAddExtraMeal } from "@/hooks/requests/plan/use-add-extra-meal";
import { useAddMealSlot } from "@/hooks/requests/plan/use-add-meal-slot";
import { useToast } from "@/hooks/ui/use-toast";
import { planAdjustmentIntentAtom } from "@/stores/plan-adjustment-modal.store";
import { cn } from "@/utils/cn";
import { useSetAtom } from "jotai";
import { memo, useCallback, useEffect, useState } from "react";
import { SavedFoodsPickerList } from "./saved-foods-picker-list";

type AddFromSavedFoodsMode = "extra-food" | "add-to-meal-time";

interface AddFromSavedFoodsModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  onComplete: () => void;
  day?: string;
  targetDate?: string;
  mode?: AddFromSavedFoodsMode;
  mealTime?: MealTime;
}

const getNormalizedDay = (day?: string) => {
  if (day) {
    return day.toLowerCase();
  }

  return new Date().toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
};

export const AddFromSavedFoodsModal = memo(function AddFromSavedFoodsModal({
  isOpen,
  onDismiss,
  onComplete,
  day,
  targetDate,
  mode = "extra-food",
  mealTime,
}: AddFromSavedFoodsModalProps) {
  const [selectedTab, setSelectedTab] = useState<SavedMealsTabValue>("your-food");
  const [submittingMealId, setSubmittingMealId] = useState<string | null>(null);
  const { addExtraMealAsync } = useAddExtraMeal();
  const { addMealSlotAsync } = useAddMealSlot();
  const setPlanAdjustmentIntent = useSetAtom(planAdjustmentIntentAtom);
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    if (!isOpen) {
      setSubmittingMealId(null);
      return;
    }

    setSelectedTab("your-food");
  }, [isOpen]);

  const handleSelectMeal = useCallback(
    async (meal: Meal) => {
      if (submittingMealId) {
        return;
      }

      const mealId = meal.id;
      if (!mealId) {
        return;
      }

      setSubmittingMealId(mealId);
      try {
        if (mode === "add-to-meal-time" && mealTime) {
          const normalizedDay = getNormalizedDay(day);
          const result = (await addMealSlotAsync({
            day: normalizedDay,
            mealTime,
            meal_id: mealId,
            meal_name: meal.name ?? undefined,
            targetDate: targetDate ?? undefined,
          })) as Record<string, unknown> | undefined;

          if (result?.needs_adjustment) {
            setPlanAdjustmentIntent({
              kind: "add-meal-to-slot",
              day: normalizedDay,
              mealTime,
              mealId,
              mealName: meal.name ?? undefined,
              targetDate: targetDate ?? undefined,
              canUndo: Boolean(result?.is_undoable),
            });
          } else {
            showSuccess("Meal added to your plan");
          }

          onComplete();
        } else {
          await addExtraMealAsync({
            day: getNormalizedDay(day),
            meal_id: mealId,
            meal_name: meal.name ?? "Extra Food",
            targetDate: targetDate ?? undefined,
          });

          showSuccess("Food added to Extra Food");
          onComplete();
        }
      } catch (error) {
        console.error("Failed to add food from saved meals:", error);
        showError("Failed to add food. Please try again.");
      } finally {
        setSubmittingMealId(null);
      }
    },
    [
      addExtraMealAsync,
      addMealSlotAsync,
      day,
      mealTime,
      mode,
      onComplete,
      setPlanAdjustmentIntent,
      showError,
      showSuccess,
      submittingMealId,
      targetDate,
    ],
  );

  return (
    <BaseModal
      isOpen={isOpen}
      onDismiss={onDismiss}
      backdropDismiss={true}
      initialBreakpoint={1}
      breakpoints={[0, 1]}
      scrollY={false}
      handle={true}
      forceOverscroll={false}
    >
      <div className="safe-area-top safe-area-bottom relative mx-auto flex h-full max-w-[450px] flex-col bg-white">
        <div
          key={selectedTab}
          className={cn(
            "absolute top-[calc(var(--safe-area-inset-top)+3.75rem)] right-0 left-0 z-0 h-10 w-full blur-3xl",
            {
              "bg-[radial-gradient(circle_at_center,var(--color-your-foods)_0%,var(--color-your-foods)_35%)]":
                selectedTab === "your-food",
            },
          )}
        />

        <div className="relative z-10 flex flex-1 flex-col overflow-hidden">
          <div className="flex items-center gap-2 px-4 pt-8 pb-4">
            <TextButton
              type="button"
              onClick={onDismiss}
              aria-label="Back to add food options"
              className="flex h-11 w-11 shrink-0 items-center justify-center"
            >
              <ChevronDownIcon className="h-11 w-11 stroke-black" />
            </TextButton>

            <SegmentedControl
              options={SAVED_MEALS_TAB_OPTIONS}
              value={selectedTab}
              onChange={(value) => setSelectedTab(value as SavedMealsTabValue)}
              classNames={{
                container: "flex-1 bg-white",
              }}
            />
          </div>

          <div className="ion-content-scroll-host no-scrollbar relative flex-1 overflow-y-auto overscroll-contain px-4 py-4 pb-24">
            <SavedFoodsPickerList
              mode={selectedTab}
              addingMealId={submittingMealId}
              onAddClick={handleSelectMeal}
            />
          </div>
        </div>
      </div>
    </BaseModal>
  );
});
